import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, PartyPopper, X } from 'lucide-react';
import { useSetupProgress } from '../../hooks/useSetupProgress';
import { useOnboardingState } from '../../hooks/useOnboardingState';
import { useRole } from '../../context/RoleContext';

const COLLAPSE_KEY = 'fds_checklist_collapsed';

/**
 * Getting-started checklist for the dashboard.
 *
 * Deliberately a checklist rather than an anchored spotlight tour: steps are
 * derived from real data, so nothing breaks when a screen is empty, a role
 * hides a button, or the user did half of this before the tour appeared. It
 * also survives being ignored — someone who comes back in week three sees
 * exactly what's still outstanding.
 */
export const GettingStartedChecklist = () => {
    const { steps, loading, completed, total, allDone } = useSetupProgress();
    const { state, patch } = useOnboardingState();
    const { can } = useRole();
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
    });

    // Every step lands on a screen only an owner can open, so anyone else would
    // get a list of doors that don't open for them.
    if (!can('dashboard')) return null;
    if (loading || state.checklist_dismissed || steps.length === 0) return null;

    const dismiss = () => patch({ checklist_dismissed: true });

    const toggle = () => {
        setCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
            return next;
        });
    };

    if (allDone) {
        return (
            <section className="fd-card fd-card-quiet p-4 flex items-center gap-3.5">
                <span className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-gold-fill/15">
                    <PartyPopper className="w-5 h-5 text-gold" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-fg">Setup complete.</p>
                    <p className="text-fd-2xs text-fg-3">
                        Your numbers are live. Anything else you need is in{' '}
                        <Link to="/help" className="text-gold underline underline-offset-2">Help &amp; Guides</Link>.
                    </p>
                </div>
                <button
                    onClick={dismiss}
                    className="fd-btn fd-btn-secondary fd-btn-sm shrink-0"
                >
                    Done
                </button>
            </section>
        );
    }

    const percent = Math.round((completed / total) * 100);

    return (
        <section className="fd-card fd-card-quiet overflow-hidden" aria-label="Finish setting up">
            <header className="flex items-center gap-3 px-4 py-3.5">
                <button
                    onClick={toggle}
                    aria-expanded={!collapsed}
                    className="flex-1 flex items-center gap-3 min-w-0 text-left"
                >
                    {collapsed
                        ? <ChevronRight className="w-4 h-4 shrink-0 text-fg-4" />
                        : <ChevronDown className="w-4 h-4 shrink-0 text-fg-4" />}
                    <span className="min-w-0">
                        <span className="block text-sm font-semibold text-fg">
                            Finish setting up
                        </span>
                        <span className="block text-fd-2xs text-fg-3">
                            {completed} of {total} done
                        </span>
                    </span>
                </button>

                <div className="hidden sm:block w-24 h-1.5 rounded-full bg-sunken overflow-hidden shrink-0" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label="Setup progress">
                    <div
                        className="h-full bg-gold-fill transition-[width] duration-500"
                        style={{ width: `${percent}%` }}
                    />
                </div>

                <button
                    onClick={dismiss}
                    aria-label="Hide setup checklist"
                    className="fd-btn fd-btn-icon shrink-0"
                >
                    <X className="w-4 h-4" />
                </button>
            </header>

            {!collapsed && (
                <ul className="px-2 pb-2">
                    {steps.map((step) => (
                        <li key={step.id}>
                            {step.done ? (
                                <div className="flex items-start gap-3 px-3 py-2.5 rounded-lg">
                                    <span className="shrink-0 w-5 h-5 mt-0.5 rounded-full grid place-items-center bg-success-fill">
                                        <Check className="w-3 h-3 text-fg" strokeWidth={3} />
                                    </span>
                                    <span className="text-fd-sm-tight text-fg-4 line-through decoration-1">
                                        {step.label}
                                    </span>
                                </div>
                            ) : (
                                <Link
                                    to={step.to}
                                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-card transition-colors group"
                                >
                                    <span className="shrink-0 w-5 h-5 mt-0.5 rounded-full border-2 border-fg-4 group-hover:border-gold transition-colors" />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-fd-sm-tight font-medium text-fg">
                                            {step.label}
                                        </span>
                                        <span className="block text-fd-2xs text-fg-3 leading-snug mt-0.5">
                                            {step.hint}
                                        </span>
                                    </span>
                                    <ChevronRight className="shrink-0 w-4 h-4 mt-1 text-fg-4 group-hover:text-gold transition-colors" />
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};
